import ComponentElement from 'components/component-element';
import PhotogalleryUploaderImage from './photogallery-uploader-image';
import {createElement, declension} from 'methods/utils';

const PROGRESS_DONE = 1;

/**
 * Загрузчик файлов
 * @param {Element} $element Контейнер для звёзд
 */
function PhotogalleryUploader($element, options = {}) {
    ComponentElement.call(this, $element, options);

    // хранилище фоток
    this.images = [];

    this.$images  = this.$element.querySelector('.js-uploaderImages');
    this.$open    = this.$element.querySelector('.js-uploaderOpen');
    this.$input   = this.$element.querySelector('input[type="file"]');
    this.$wrapper = this.$element.querySelector('.js-uploaderWrapper');

    this.$input.addEventListener('change', this.onChange.bind(this));

    this.$open.addEventListener('click', e => {
        this.$input.click();
    });

    this.$element.addEventListener('dragenter', e => {
        e.stopPropagation();
        e.preventDefault();
    });

    this.$wrapper.addEventListener('dragleave', e => {
        e.stopPropagation();
        e.preventDefault();
    });

    this.$wrapper.addEventListener('dragover', e => {
        e.stopPropagation();
        e.preventDefault();
    });

    this.$wrapper.addEventListener('drop', e => {
        e.stopPropagation();
        e.preventDefault();

        [...e.dataTransfer.files].forEach(this.addFile.bind(this));
    });
}

PhotogalleryUploader.prototype = Object.create(ComponentElement.prototype);
Object.defineProperty(PhotogalleryUploader.prototype, 'constructor', {
    value: PhotogalleryUploader,
    enumerable: false,
    writable: true,
});

PhotogalleryUploader.prototype.setOptions = function(options) {
    ComponentElement.prototype.setOptions.call(this, Object.assign({
        pid:           parseInt(this.$element.dataset.pid, 10) || 0,
        mid:           parseInt(this.$element.dataset.mid, 10) || 0,
        accept:        this.$element.dataset.accept            || undefined,
        maxFileSize:   this.$element.dataset.maxFileSize       || 20 * 1024 * 1024, // 20mb
        maxFilesCount: this.$element.dataset.maxFilesCount     || 12,
        exclude:       this.$element.dataset.exclude           || [],
        hash:          this.$element.dataset.hash,
    }, options));

    if (typeof this.options.accept == 'string') {
        this.options.accept = this.options.accept.split(',');
    }

    if (typeof this.options.exclude == 'string') {
        this.options.exclude = this.options.exclude.split(',');
    }
}

/**
 * Создание превью
 * @param {File} file Файл, загруженный в инпут
 */
PhotogalleryUploader.prototype.addFile = function(file) {
    if (!(file instanceof File)) {
        throw new TypeError('File should be of type File');
    }

    // превышает количество
    if (this.images.length >= this.options.maxFilesCount) {
        throw new Error(`Не более ${this.options.maxFilesCount} ${declension(this.options.maxFilesCount, 'фотография', 'фотографии', 'фотографий')}`);
    }

    // некорректный формат файла
    if (Array.isArray(this.options.accept) && !~this.options.accept.indexOf(file.type)) {
        throw new Error(`Допустимые форматы ${this.options.accept.map(type => type.split('/')[1]).join(', ').toUpperCase()}`);
    }

    // превышает размер
    if (file.size > this.options.maxFileSize) {
        throw new Error('Превышен допустимый размер файла');
    }

    const image = PhotogalleryUploaderImage.fromFile(file);
    image.on('remove', this.onImageRemoveEvent.bind(this, image));
    this.images.push(image);
    this.$images.appendChild(image.$element);

    return this.upload(image);
}

PhotogalleryUploader.prototype.onImageRemoveEvent = function(image) {
    const $input = this.$element.querySelector(`input[type="hidden"][value="${image.id}"]`);
    if ($input) {
        $input.remove();
    }

    this.images.splice(this.images.indexOf(image), 1);
    this.emit('removed', image);
}

PhotogalleryUploader.prototype.addImage = function(id, src) {
    const image = new PhotogalleryUploaderImage({id, src});
    image.on('remove', this.onImageRemoveEvent.bind(this, image));
    this.images.push(image);
    this.$images.appendChild(image.$element);
    this.createImageHiddenInput(id);
    return image;
}

PhotogalleryUploader.prototype.createImageHiddenInput = function(id) {
    this.$element.insertAdjacentHTML('afterbegin', `<input type="hidden" name="photos[]" value="${id}">`);
}

/**
 * Загрузка изображений на сервер
 * @return {Promise} Промис, по завершении которого загружены все файлы
 */
PhotogalleryUploader.prototype.upload = function(image) {
    const formData = new FormData();
    formData.append('pid', this.options.pid);
    formData.append('mid', this.options.mid);
    formData.append('accept', this.options.accept.join(','));
    formData.append('maxFileSize', this.options.maxFileSize);
    formData.append('maxFilesCount', this.options.maxFilesCount);
    formData.append('exclude', this.options.exclude.join(','));
    formData.append('hash', this.options.hash);
    formData.append('file', image.file, image.file.name);

    image.setProgress(0.3);
    this.emit('upload', image);

    return fetch('ajax_upload/', {
            method: 'POST',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
        })
        .then(response => response.json())
        .then(r => {
            if (r.status != 'ok') {
                throw new Error(r.message);
            }

            return r.data;
        })
        .catch(e => {
            alert(e.message);
            image.remove();
        })
        .then(data => {
            image.setProgress(PROGRESS_DONE);
            image.setId(data.id);
            image.setSrc(data.url);
            this.createImageHiddenInput(data.id);
            this.emit('uploaded', image);
        });
}

PhotogalleryUploader.prototype.hasUploadingImages = function(image) {
    return this.images.some(image => image.getProgress() != PROGRESS_DONE);
}

/**
 * Очистка списка
 */
PhotogalleryUploader.prototype.reset = function() {
    this.images = [];

    while (this.$images.lastElementChild) {
        this.$images.removeChild(this.$images.lastElementChild);
    }
}

/**
 * Событие выбора файлов
 * @param {Event} e DOM событие
 */
PhotogalleryUploader.prototype.onChange = function(e) {
    try {
        let files = [...this.$input.files];

        if (this.options.maxFilesCount) {
            files = files.slice(0, this.options.maxFilesCount);
        }

        files.forEach(this.addFile.bind(this));
    } catch (e) {
        alert(e);
    }
    this.$input.value = '';
}

export default PhotogalleryUploader;
